import { all, call, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import {
  incrementIndex,
  IRegisterReducer,
  next,
  prev,
  proceed,
  registerAction,
  registerActionState,
  registerModule,
} from 'core/slices/register';
import { EntityTypes } from 'constants/entities';
import { updateState } from 'core/actions/product/state';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { AssetType, IAsset } from 'types/app/asset';
import { LoadingState } from 'types/domain/base';
import { loadAsset } from 'core/services/asset';
import { ActionType, IActionBasic } from 'types/domain/action';
import { IPlayerState, updatePlayerState, updateVideoState } from 'core/slices/player';
import { selectShouldPause } from 'core/selectors/product/shouldPause';
import { PayloadAction } from '@reduxjs/toolkit';
import { selectPortrait } from 'core/selectors/product/portrait';
import { selectAction } from 'core/selectors/product/action/action';

function* nextActionSaga() {
  const { feed, index }: IActionFeed = yield select(selectActionFeed);

  const page = feed[index];
  let nextPage = feed[index + 1];
  const shouldPause = yield select(selectShouldPause);

  if (!page) return;

  const register: IRegisterReducer = yield select((state: RootState) => state.register);
  if (shouldPause) yield put(yield call(updatePlayerState, { paused: true, culprit: 9 }));
  const actionId = yield select((state: RootState) => state.state[EntityTypes.ACTION]);

  if (!nextPage) return;
  if (nextPage.actionId) {
    if (nextPage.actionId !== actionId) {
      yield put(yield call(updateState, { type: EntityTypes.ACTION, id: nextPage.actionId, culprit: 6 }));
      if (!register.isPrev) yield take([registerAction, registerActionState]);
    }
  }

  if (!nextPage.actionId && nextPage.moduleId)
    yield put(yield call(registerModule, { moduleId: nextPage.moduleId, sessionAttemptId: nextPage.sessionAttemptId }));

  yield put(yield call(proceed));
}
//this moves the page on when the next action is registered. If it has a scene wait for that scene to load.
function* proceedSaga() {
  const { feed, index } = yield select(selectActionFeed);

  const current: IActionFeedItem = feed[index];
  const next: IActionFeedItem = feed[index + 1];
  const time = 100;

  if (!next || !current) return;

  if (next.verificationRequestId || next.courseNodeId) yield delayedIncrement(time);
  else if (next.moduleId && !next.actionId) yield delayedIncrement(time);
  else if (next.actionId) {
    //@ts-ignore
    const currentAction: IActionBasic = yield select(state => selectAction(state, current.actionId));
    //@ts-ignore
    const nextAction: IActionBasic = yield select(state => (next.actionId ? selectAction(state, next.actionId) : null));

    if (!nextAction.scene) yield delayedIncrement(time);
    else {
      if (currentAction && currentAction.isMultichoice) return yield delayedIncrement(500);

      if (nextAction && currentAction && currentAction.sceneId && currentAction.sceneId === nextAction.sceneId)
        return yield put(yield call(incrementIndex));

      //@ts-ignore
      let asset: IAsset = yield select(state => selectSceneAsset(state, nextAction.sceneId));
      const loadRquired = [ActionType.INTERACTIVE].includes(nextAction.type) || asset?.type !== AssetType.IMAGE;

      while (!asset || (asset.loading !== LoadingState.LOADED && loadRquired)) {
        yield take(loadAsset.fulfilled);
        //@ts-ignore
        asset = yield select(state => selectSceneAsset(state, nextAction.sceneId));
      }

      if (asset.type === AssetType.VIDEO) {
        let ready = yield select((state: RootState) => state.core.player.state.video.ready);

        while (!ready) {
          yield take(updateVideoState);
          ready = yield select((state: RootState) => state.core.player.state.video.ready);
        }
      }
      yield delayedIncrement(time);
    }
  }
}

function* interactSaga(action: PayloadAction<number>) {
  const state: IPlayerState = yield select((state: RootState) => state.core.player.state);
  const portrait = yield select(selectPortrait);

  if (portrait && action.payload < 0.3) yield put(yield call(prev));
  else if (portrait && action.payload > 0.7) yield put(yield call(next, { culprit: 'interact' }));
}

function* delayedIncrement(time = 500) {
  yield delay(time);
  yield put(yield call(incrementIndex));
}

function* watchProceed() {
  yield takeEvery(proceed, proceedSaga);
}

function* watchNext() {
  yield takeEvery(next, nextActionSaga);
}

function* watchInteract() {
  //yield takeEvery(interact, interactSaga);
}

export function* nextPageSaga() {
  yield all([fork(watchNext), fork(watchProceed), fork(watchInteract)]);
}
